import { useState, useEffect, useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import { toast } from 'sonner';
import {
  deleteOrder as deleteOrderRequest,
  getOrders,
  updateOrder as updateOrderRequest,
} from '@/api/orders';
import { formatDateForBackend, getDateOffset } from '@/utils/dateUtils';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';

const DELIVERY_HISTORY_DAYS = 14;
const DELIVERY_ORDER_LIMIT = 1000;

const mergeOrdersById = (current, incoming) => {
  const merged = new Map(current.map((order) => [order.id, order]));
  incoming.forEach((order) => merged.set(order.id, order));
  return Array.from(merged.values());
};

/**
 * Delivery order state.
 *
 * Connected mode loads the recent operational window once and exact older
 * dates on demand. Offline mode retains the original all-local-cache read; the
 * desktop cache is already bounded and must remain complete for an outage.
 */
const useOrders = ({ selectedDateString = null, offline = false } = {}) => {
  const revision = useSyncExternalStore(
    subscribeActiveSessionRevision, getActiveSessionRevision, getActiveSessionRevision,
  );
  // Object identity also distinguishes online -> offline -> online callbacks.
  const scope = useMemo(() => ({ revision, offline }), [revision, offline]);
  const [view, setView] = useState({ orders: [], covered: new Set(), excluded: new Set() });
  const selectedDateRef = useRef(selectedDateString);
  const cycleRef = useRef(null);
  const rollingWindowStart = useMemo(
    () => formatDateForBackend(getDateOffset(-DELIVERY_HISTORY_DAYS)),
    [],
  );

  selectedDateRef.current = selectedDateString;

  const ownsCycle = useCallback((cycle) => Boolean(
    cycle && cycle.active && cycleRef.current === cycle
    && cycle.scope.revision === getActiveSessionRevision()
  ), []);

  const publish = useCallback((cycle, update) => {
    if (!ownsCycle(cycle)) return;
    setView((current) => (
      ownsCycle(cycle) && current.cycle === cycle ? update(current) : current
    ));
  }, [ownsCycle]);

  const readOrders = useCallback(async (cycle, deliveryDate = null, options = {}) => {
    if (!ownsCycle(cycle)) return;
    const request = { sequence: ++cycle.sequence };
    const previous = deliveryDate ? cycle.dates.get(deliveryDate) : cycle.bulk;
    // Superseded requests no longer own a loading indicator, even if they hang.
    cycle.pending.delete(previous);
    if (deliveryDate) cycle.dates.set(deliveryDate, request);
    else cycle.bulk = request;
    cycle.pending.add(request);
    const ownsRequest = () => ownsCycle(cycle) && (options.current?.() ?? true) && (
      deliveryDate ? cycle.dates.get(deliveryDate) === request : cycle.bulk === request
    );
    publish(cycle, (current) => ({ ...current, loading: true }));
    try {
      const params = cycle.scope.offline ? undefined : deliveryDate
        ? { delivery_date: deliveryDate }
        : { date_from: rollingWindowStart, limit: DELIVERY_ORDER_LIMIT };
      const execute = config => {
        if (!ownsRequest()) throw new Error('Retired order observation');
        return getOrders(params, ...(config ? [config] : []));
      };
      const incoming = await (options.read ? options.read(execute, options.config) : execute(options.config));
      if (!ownsRequest()) return { state: 'retired' };
      if (!Array.isArray(incoming) || incoming.some(item => typeof item?.id !== 'string')) throw new Error('Invalid order list');
      publish(cycle, (current) => {
        if (!ownsRequest()) return current;
        if (deliveryDate) {
          // Date queries can overlap by ID when an order moves between dates.
          // Retain receipts for removed rows too: an older other-date result
          // must not resurrect a row absent from a newer accepted refresh.
          const receipts = new Map(current.receipts);
          const retained = current.orders.filter((order) => {
            if (order.delivery_date !== deliveryDate
              || receipts.get(order.id) > request.sequence) return true;
            receipts.set(order.id, request.sequence);
            return false;
          });
          const accepted = incoming.filter((order) => (
            !(receipts.get(order.id) > request.sequence)
          ));
          accepted.forEach((order) => receipts.set(order.id, request.sequence));
          return {
            ...current,
            orders: mergeOrdersById(retained, accepted),
            receipts,
            covered: new Set([...current.covered, deliveryDate]),
          };
        }
        if (cycle.scope.offline) {
          const retained = current.orders.filter(order => current.receipts.get(order.id) > request.sequence);
          const accepted = incoming.filter(order => !(current.receipts.get(order.id) > request.sequence));
          return { ...current, orders: mergeOrdersById(accepted, retained), initialLoaded: true };
        }
        // Exact-date attempts claim their date even on failure or empty success.
        // Bulk completion must not resurrect their older rows or claim coverage.
        const excluded = new Set(cycle.dates.keys());
        const retained = current.orders.filter((order) => (
          excluded.has(order.delivery_date) || order.delivery_date < rollingWindowStart
          || current.receipts.get(order.id) > request.sequence
        ));
        const receipts = new Map(current.receipts);
        const accepted = incoming.filter((order) => (
          !excluded.has(order.delivery_date) && !(receipts.get(order.id) > request.sequence)
        ));
        accepted.forEach((order) => receipts.set(order.id, request.sequence));
        return {
          ...current, orders: mergeOrdersById(accepted, retained), initialLoaded: true, excluded, receipts,
        };
      });
      if (!cycle.scope.offline && !deliveryDate && incoming.length >= DELIVERY_ORDER_LIMIT) {
        toast.error('The Delivery order window reached its safety limit. Some orders may be missing.');
      }
      return { state: 'refreshed', ids: incoming.map(item => item.id), deliveryDate };
    } catch (error) {
      if (!ownsRequest()) return { state: 'retired' };
      console.error('Error fetching orders:', error);
      toast.error('Failed to fetch orders');
      return { state: 'unavailable' };
    } finally {
      cycle.pending.delete(request);
      publish(cycle, (current) => ({ ...current, loading: cycle.pending.size > 0 }));
    }
  }, [ownsCycle, publish, rollingWindowStart]);

  // Refresh only the selected date after a map/run mutation. This callback is
  // intentionally stable across date changes; it reads the current date ref.
  const fetchOrders = useCallback(async (options = {}) => {
    const cycle = cycleRef.current;
    if (!ownsCycle(cycle) || cycle.scope !== scope) return;
    const deliveryDate = options.deliveryDate ?? selectedDateRef.current;
    if (offline || deliveryDate) return readOrders(cycle, offline ? null : deliveryDate, options);
    return { state: 'unavailable' };
  }, [ownsCycle, offline, readOrders, scope]);

  // Only the acknowledged field is adopted. Stamp before any older list can
  // publish, including offline bulk responses and absent-row observations.
  const acceptDispatchOrder = useCallback((item, kind) => {
    const cycle = cycleRef.current;
    if (!ownsCycle(cycle) || cycle.scope !== scope) return;
    const sequence = ++cycle.sequence;
    publish(cycle, current => {
      const receipts = new Map(current.receipts); receipts.set(item.id, sequence);
      return { ...current, receipts, orders: current.orders.map(order => order.id !== item.id ? order
        : kind === 'note' ? { ...order, dispatch_note: item.dispatch_note ?? '' }
          : { ...order, desktop_sync: { ...order.desktop_sync, offline_map: item.desktop_sync.offline_map,
            offline_map_cleared_at: item.desktop_sync.offline_map_cleared_at } }) };
    });
  }, [ownsCycle, publish, scope]);

  // Legacy mutation helpers retain their existing contract; production MapView
  // uses only the read/refresh interface. Mutation ownership is a separate leaf.
  const setOrders = useCallback((update) => {
    setView((current) => ({ ...current, orders: update(current.orders) }));
  }, []);

  const deleteOrder = useCallback(async (orderId) => {
    try {
      await deleteOrderRequest(orderId);
      setOrders((current) => current.filter((order) => order.id !== orderId));
      toast.success('Order deleted');
      return true;
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error('Failed to delete order');
      return false;
    }
  }, [setOrders]);

  const updateOrder = useCallback(async (orderId, data) => {
    try {
      const updated = await updateOrderRequest(orderId, data);
      setOrders((current) => current.map((order) => (
        order.id === orderId ? updated : order
      )));
      toast.success('Order updated');
      return updated;
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order');
      return null;
    }
  }, [setOrders]);

  const addOrders = useCallback((newOrders) => {
    setOrders((current) => mergeOrdersById(current, newOrders));
  }, [setOrders]);

  // Load the bounded operational window. Connectivity changes remount the app
  // in normal operation, but resetting here also keeps the hook truthful in
  // tests and any future in-place transition.
  useEffect(() => {
    const cycle = { scope, active: true, dates: new Map(), pending: new Set(), bulk: null, sequence: 0 };
    cycleRef.current = cycle;
    setView({ cycle, orders: [], covered: new Set(), excluded: new Set(), receipts: new Map(), initialLoaded: false, loading: true });
    readOrders(cycle);
    return () => {
      cycle.active = false;
    };
  }, [scope, readOrders]);

  // Older dates are fetched once per mounted session. Results are merged under
  // their requested date, so a late response cannot change the visible date.
  useEffect(() => {
    const cycle = cycleRef.current;
    if (
      offline
      || !selectedDateString
      || selectedDateString >= rollingWindowStart
      || !ownsCycle(cycle)
      || cycle.scope !== scope
      || (view.cycle === cycle && view.covered.has(selectedDateString))
      || cycle.pending.has(cycle.dates.get(selectedDateString))
    ) {
      return undefined;
    }
    readOrders(cycle, selectedDateString);
    return undefined;
    // Retry failed dates on revisit or explicit refresh, not on state updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    offline,
    ownsCycle,
    readOrders,
    rollingWindowStart,
    selectedDateString,
    scope,
  ]);

  // Hide the previous owner's projection during render, before effect cleanup.
  const visible = view.cycle?.scope === scope && ownsCycle(view.cycle);
  const selectedDateLoaded = Boolean(
    visible && selectedDateString && (offline ? view.initialLoaded
      : view.covered.has(selectedDateString)
        || (selectedDateString >= rollingWindowStart && view.initialLoaded
          && !view.excluded.has(selectedDateString)))
  );

  return {
    orders: visible ? view.orders : [],
    loading: visible ? view.loading : true,
    selectedDateLoaded,
    rollingWindowStart,
    fetchOrders,
    acceptDispatchOrder,
    deleteOrder,
    updateOrder,
    addOrders,
  };
};

export default useOrders;
